/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentList } from '../../components/documents/document-list.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import {
  type TherapistDocument,
  therapistDocumentsApi,
} from '../../lib/api/therapist-documents.js';
import { useMyDocuments } from '../../lib/queries/therapist-documents.js';

/** Therapist portal → My Documents (BRD v2.0 CR-012 #3): the therapist's own HR documents. */
export function TherapistDocumentsPage(): JSX.Element {
  const { t } = useTranslation();
  const query = useMyDocuments();
  const onDownload = useCallback((doc: TherapistDocument) => {
    void therapistDocumentsApi.downloadMine(doc);
  }, []);

  return (
    <div className="p-8">
      <PageHeader title={t('documents.myTitle')} subtitle={t('documents.mySubtitle')} />
      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={(query.data ?? []).length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('documents.empty')}
      >
        <DocumentList docs={query.data ?? []} onDownload={onDownload} />
      </QueryState>
    </div>
  );
}
